import {
  DocumentNode,
  OperationVariables,
  TypedDocumentNode,
  QueryHookOptions,
  QueryResult,
  useQuery,
} from '@apollo/client';
import { useEffect, useRef, useState } from 'react';

const POLLING_INTERVAL = 500;
const POLLING_TIMEOUT = 5000;

interface UseQueryPollingUntilOptions<
  TData = any,
  TVariables extends OperationVariables = OperationVariables,
> extends Omit<QueryHookOptions<TData, TVariables>, 'pollInterval'> {
  onPollingTimeout?: () => void;
}

interface UseQueryPollingUntilResult<
  TData = any,
  TVariables extends OperationVariables = OperationVariables,
> extends Omit<QueryResult<TData, TVariables>, 'startPolling' | 'stopPolling'> {
  startPolling(condition: (data?: TData) => boolean, pollInterval?: number, pollTimeout?: number): void;
  polling: boolean;
}
interface PollingState<TData> {
  condition: (data: TData) => boolean;
  timeout: number;
  interval: number;
}

export const useQueryPollingUntil = <TData = any, TVariables extends OperationVariables = OperationVariables>(
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options?: UseQueryPollingUntilOptions<TData, TVariables>,
): UseQueryPollingUntilResult<TData, TVariables> => {
  const pollingTimeout = useRef<any>(null);
  const [polling, setPolling] = useState<PollingState<TData> | undefined>(undefined);

  const queryResult = useQuery<TData, TVariables>(query, {
    pollInterval: polling?.interval || 0,
    ...options,
  });

  useEffect(() => {
    if (polling) {
      pollingTimeout.current = setTimeout(() => {
        if (options?.onPollingTimeout) {
          options.onPollingTimeout();
        }
        setPolling(() => undefined);
      }, polling.timeout);
    } else {
      clearTimeout(pollingTimeout.current);
    }
    return () => {
      clearTimeout(pollingTimeout.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [polling]);

  useEffect(() => {
    if (polling && queryResult.data) {
      const { condition } = polling;
      // condition met stop polling
      if (condition(queryResult.data)) {
        setPolling(() => undefined);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryResult.data]);

  return {
    ...queryResult,
    loading: !!polling || queryResult.loading,
    polling: !!polling,
    startPolling: (condition, pollInterval, pollTimeout) => {
      setPolling({
        interval: pollInterval || POLLING_INTERVAL,
        timeout: pollTimeout || POLLING_TIMEOUT,
        condition,
      });
    },
  };
};
